(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DURATION = 850;
  const EASE = 'cubic-bezier(.2,.72,.2,1)';
  const text = (root, selector, value = '') => {
    const node = root.querySelector(selector);
    if (node && node.textContent !== String(value)) node.textContent = value;
  };
  const readJSON = (node, fallback) => {
    try { return JSON.parse(node?.textContent || '') || fallback; }
    catch { return fallback; }
  };
  const imageURL = value => typeof value === 'string' ? value : value?.url || '';
  const own = (object, key) => object && Object.prototype.hasOwnProperty.call(object, key) ? object[key] : null;

  function mount(root) {
    if (root.dataset.saleMotionMounted === 'true') return;
    const stage = root.querySelector('[data-sr-stage]');
    if (!stage) return;
    root.dataset.saleMotionMounted = 'true';

    const all = [...root.querySelectorAll('[data-sr-item]')];
    const map = readJSON(root.querySelector('[data-sr-model-map]'), {});
    const choices = new Map(all.map(item => [item, readJSON(item.querySelector('[data-sr-colors]'), [])]));
    const colorRow = root.querySelector('[data-sr-color-row]');
    const colorSelect = root.querySelector('[data-sr-color]');
    const detail = root.querySelector('[data-sr-detail]');
    const detailImage = root.querySelector('[data-sr-detail-image]');
    const caption = root.querySelector('.sr-caption');
    const previousButton = root.querySelector('[data-sr-prev]');
    const nextButton = root.querySelector('[data-sr-next]');
    const openButton = root.querySelector('[data-sr-open]');
    const abort = new AbortController();
    const listen = (node, type, callback, options = {}) => node?.addEventListener(type, callback, { ...options, signal: abort.signal });
    let visible = [...all];
    let index = 0;
    let shown = null;
    let transitionID = 0;
    let animations = [];
    let animationTimer = 0;
    let closeTimer = 0;
    let releaseTimer = 0;
    let cancelLoad = null;
    const warmModels = new Map();
    let drag = null;
    let suppressClick = false;
    let opener = null;

    const center = document.createElement('button');
    center.type = 'button';
    center.className = 'sr-motion-center';
    center.dataset.srModelButton = '';
    const baseImage = document.createElement('img');
    const incomingImage = document.createElement('img');
    [baseImage, incomingImage].forEach(image => {
      image.className = 'sr-motion-model';
      image.draggable = false;
      image.decoding = 'async';
      image.width = 900;
      image.height = 1200;
      center.append(image);
    });
    incomingImage.hidden = true;
    const sprites = document.createElement('div');
    sprites.className = 'sr-motion-sprites';
    sprites.setAttribute('aria-hidden', 'true');
    stage.append(center, sprites);

    let empty = root.querySelector('[data-sr-empty]');
    if (!empty) {
      empty = document.createElement('div');
      empty.className = 'sr-empty';
      empty.dataset.srEmpty = '';
      const title = document.createElement('h2');
      title.textContent = 'Neue Angebote folgen.';
      const copy = document.createElement('p');
      copy.textContent = 'Entdecke bis dahin unsere aktuellen Pieces.';
      const link = document.createElement('a');
      link.href = root.dataset.allPiecesUrl || '/collections/nla-originals';
      link.className = 'sr-pill';
      link.textContent = 'Alle Pieces entdecken ↗';
      empty.append(title, copy, link);
      stage.before(empty);
    }
    empty.hidden = true;
    const itemNow = () => visible[index];
    const count = () => `${String(index + 1).padStart(2, '0')} / ${String(visible.length).padStart(2, '0')}`;

    function matched(item, color = item.dataset.color) {
      return own(own(map, item.dataset.productId), color);
    }
    function applyMapping(item) {
      const entry = matched(item);
      if (!entry) return;
      for (const side of ['front', 'back']) {
        if (imageURL(entry[side])) item.dataset[side] = imageURL(entry[side]);
      }
      if (entry.printSide === 'front' || entry.printSide === 'back') item.dataset.side = entry.printSide;
      item.dataset.model = imageURL(entry.model);
      item.dataset.modelAlt = entry.modelAlt || '';
    }
    function snapshot(item) {
      applyMapping(item);
      const entry = matched(item);
      const side = item.dataset.side === 'back' ? 'back' : 'front';
      const garment = imageURL(entry?.garment) || item.dataset.garment || item.dataset[side] || '';
      const model = imageURL(entry?.model) || item.dataset.model || '';
      return {
        item, garment, model,
        source: model || garment,
        isModel: Boolean(model),
        alt: model ? String(entry?.modelAlt || item.dataset.modelAlt || `${item.dataset.title} · ${item.dataset.color} am Model`).replace(/\bKI[-\s]*Styling(?:ansicht(?:en)?)?/gi,"Stylingansicht").replace(/\bAI[-\s]*(?:generated|styling|model)/gi,"Stylingansicht") : `${item.dataset.title} · ${item.dataset.color} · ${side === 'back' ? 'Rückseite' : 'Vorderseite'}`
      };
    }
    function assignImage(image, look) {
      image.onerror = () => {
        image.onerror = null;
        if (look.garment && image.getAttribute('src') !== look.garment) {
          image.src = look.garment;
          image.alt = `${look.item.dataset.title} · ${look.item.dataset.color} · Produktansicht`;
          center.classList.add('is-product-fallback');
        } else image.hidden = true;
      };
      image.alt = look.alt;
      if (look.source) {
        image.src = look.source;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }
    }
    function warmNeighbors() {
      const needed = new Set();
      if (visible.length > 1) {
        for (const step of [-1, 1]) {
          const item = visible[(index + step + visible.length) % visible.length];
          const look = snapshot(item);
          if (look.model && item !== itemNow()) needed.add(look.model);
        }
      }
      for (const [source, image] of warmModels) {
        if (!needed.has(source)) { image.removeAttribute('src'); warmModels.delete(source); }
      }
      for (const source of needed) {
        if (warmModels.has(source)) continue;
        const image = new Image();
        image.decoding = 'async';
        image.src = source;
        warmModels.set(source, image);
      }
    }
    function isReady(look) {
      return [baseImage, incomingImage, warmModels.get(look.source)].some(image => image && image.getAttribute('src') === look.source && image.complete && image.naturalWidth > 0);
    }
    function settle(look, warm = true) {
      if (!look) return;
      shown = look;
      assignImage(baseImage, look);
      incomingImage.hidden = true;
      incomingImage.removeAttribute('src');
      incomingImage.onerror = null;
      center.classList.toggle('is-product-fallback', !look.isModel);
      center.setAttribute('aria-label', `${look.item.dataset.title} · ${look.item.dataset.color} – Produkt ansehen`);
      root.classList.remove('is-changing');
      root.removeAttribute('aria-busy');
      if (warm) warmNeighbors();
    }
    function stopTransition() {
      transitionID += 1;
      clearTimeout(animationTimer);
      cancelLoad?.();
      cancelLoad = null;
      animations.forEach(animation => animation.cancel());
      animations = [];
      sprites.replaceChildren();
      root.removeAttribute('aria-busy');
      if (shown) settle(shown, false);
    }
    function loadLook(look, loader = new Image()) {
      return new Promise(resolve => {
        if (!look.source) { resolve(look); return; }
        let finished = false;
        let fallback = false;
        const finish = result => {
          if (finished) return;
          finished = true;
          loader.onload = null;
          loader.onerror = null;
          cancelLoad = null;
          resolve(result);
        };
        cancelLoad = () => { loader.removeAttribute('src'); finish(null); };
        loader.onload = () => finish(fallback ? { ...look, source: look.garment, isModel: false, alt: `${look.item.dataset.title} · ${look.item.dataset.color} · Produktansicht` } : look);
        loader.onerror = () => {
          if (!fallback && look.garment && look.garment !== look.source) {
            fallback = true;
            loader.src = look.garment;
          } else finish({ ...look, source: '', isModel: false });
        };
        if (loader.getAttribute('src') !== look.source) loader.src = look.source;
        if (loader.complete) {
          if (loader.naturalWidth > 0) loader.onload();
          else loader.onerror();
        }
      });
    }
    function sprite(source, name) {
      if (!source) return null;
      const node = document.createElement('img');
      node.className = `sr-motion-sprite ${name}`;
      node.src = source;
      node.alt = '';
      node.draggable = false;
      node.onerror = () => { node.hidden = true; };
      sprites.append(node);
      return node;
    }
    function animate(node, frames, options = {}) {
      if (!node) return;
      animations.push(node.animate(frames, { duration: DURATION, easing: EASE, fill: 'both', ...options }));
    }
    async function changeLook(previous, next, direction) {
      stopTransition();
      const token = transitionID;
      const cached = isReady(next);
      const warmed = cached ? null : warmModels.get(next.source);
      if (warmed) warmModels.delete(next.source);
      let from = previous;
      if (!cached) {
        from = { ...next, source: next.garment, isModel: false, alt: `${next.item.dataset.title} · ${next.item.dataset.color} · Produktansicht` };
        settle(from);
        if (!next.model) return;
        root.setAttribute('aria-busy', 'true');
      }
      const ready = cached ? next : await loadLook(next, warmed || new Image());
      if (!ready || token !== transitionID || abort.signal.aborted) return;
      root.removeAttribute('aria-busy');
      if (!from || reducedMotion.matches || !Element.prototype.animate || from.source === ready.source) { settle(ready); return; }
      shown = ready;
      root.classList.add('is-changing');
      assignImage(baseImage, from);
      assignImage(incomingImage, ready);
      center.classList.toggle('is-product-fallback', !ready.isModel);
      center.setAttribute('aria-label', `${ready.item.dataset.title} · ${ready.item.dataset.color} – Produkt ansehen`);
      const distance = Math.min(stage.clientWidth * .32, 425) * direction;
      const outgoing = cached ? sprite(from.garment, 'is-outgoing') : null;
      const incoming = cached ? sprite(ready.garment, 'is-incoming') : null;
      animate(outgoing, [
        { transform: 'translate(-50%, -50%) translateX(0) rotate(0deg) scale(.82)', opacity: 0, offset: 0 },
        { transform: 'translate(-50%, -50%) translateX(0) rotate(-1deg) scale(.83)', opacity: .86, offset: .22 },
        { transform: `translate(-50%, -50%) translateX(${-distance}px) translateY(-12px) rotate(${-7 * direction}deg) scale(.68)`, opacity: .18, offset: 1 }
      ]);
      animate(incoming, [
        { transform: `translate(-50%, -50%) translateX(${distance}px) translateY(-12px) rotate(${7 * direction}deg) scale(.68)`, opacity: .25, offset: 0 },
        { transform: `translate(-50%, -50%) translateX(${distance * .12}px) rotate(${-2 * direction}deg) scale(.94)`, opacity: .95, offset: .6 },
        { transform: 'translate(-50%, -50%) translateX(0) rotate(0deg) scale(.83)', opacity: .85, offset: .79 },
        { transform: 'translate(-50%, -50%) translateX(0) rotate(0deg) scale(.82)', opacity: 0, offset: 1 }
      ]);
      animate(baseImage, cached ? [{ opacity: 1, offset: 0 }, { opacity: .15, offset: .4 }, { opacity: 0, offset: .6 }, { opacity: 0, offset: 1 }] : [{ opacity: 1 }, { opacity: 0 }]);
      animate(incomingImage, cached ? [{ opacity: 0, offset: 0 }, { opacity: 0, offset: .5 }, { opacity: .1, offset: .67 }, { opacity: 1, offset: 1 }] : [{ opacity: 0 }, { opacity: 1 }]);
      animationTimer = setTimeout(() => {
        if (token !== transitionID) return;
        animations.forEach(animation => animation.cancel());
        animations = [];
        sprites.replaceChildren();
        settle(ready);
      }, DURATION + 24);
    }

    function paint() {
      const item = itemNow();
      const isEmpty = !item;
      root.classList.toggle('is-empty', isEmpty);
      empty.hidden = !isEmpty;
      stage.hidden = isEmpty;
      if (caption) caption.hidden = isEmpty;
      root.querySelectorAll('.sr-tabs').forEach(node => { node.hidden = !all.length; });
      all.forEach(button => {
        button.hidden = true;
        button.tabIndex = -1;
        button.setAttribute('aria-hidden', 'true');
        button.setAttribute('aria-current', button === item ? 'true' : 'false');
      });
      if (!item) { stopTransition(); shown = null; return; }
      const neighborPositions = visible.length === 2 ? [1] : [-1, 1];
      neighborPositions.forEach(position => {
        if (visible.length < 2) return;
        const neighbor = visible[(index + position + visible.length) % visible.length];
        const look = snapshot(neighbor);
        const image = neighbor.querySelector('.sr-fabric img');
        if (image && look.garment) {
          image.srcset = '';
          image.src = look.garment;
          image.loading = 'eager';
          image.alt = '';
        }
        neighbor.hidden = false;
        neighbor.dataset.position = String(position);
        neighbor.tabIndex = 0;
        neighbor.setAttribute('aria-hidden', 'false');
        neighbor.setAttribute('aria-label', `${position < 0 ? 'Vorheriges' : 'Nächstes'} Produkt: ${neighbor.dataset.title} · ${neighbor.dataset.color}`);
      });
      text(root, '[data-sr-title]', item.dataset.title);
      text(root, '[data-sr-price]', item.dataset.price);
      text(root, '[data-sr-counter]', count());
      if (previousButton) previousButton.disabled = visible.length < 2;
      if (nextButton) nextButton.disabled = visible.length < 2;
      if (openButton) openButton.disabled = false;
      if (colorSelect) {
        const options = choices.get(item) || [];
        colorSelect.replaceChildren(...options.map(choice => {
          const option = document.createElement('option');
          option.value = choice.color;
          option.textContent = choice.color;
          return option;
        }));
        colorSelect.value = item.dataset.color || '';
        colorSelect.disabled = options.length < 2;
        if (colorRow) colorRow.hidden = !options.length;
      }
    }
    function select(position, direction = 1) {
      if (!visible.length) return;
      const previous = shown;
      index = (position + visible.length) % visible.length;
      paint();
      changeLook(previous, snapshot(itemNow()), direction);
    }
    function move(direction) {
      if (visible.length > 1) select(index + direction, direction);
    }
    function view(side) {
      const item = itemNow();
      if (!item || !detailImage || !item.dataset[side]) return;
      detailImage.src = item.dataset[side];
      detailImage.alt = `${item.dataset.title} · ${item.dataset.color} · ${side === 'front' ? 'Vorderseite' : 'Rückseite'}`;
      root.querySelectorAll('[data-sr-view]').forEach(button => {
        const active = button.dataset.srView === side;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }
    function openDetail(trigger) {
      const item = itemNow();
      if (!item || !detail || detail.open) return;
      clearTimeout(closeTimer);
      opener = trigger;
      applyMapping(item);
      text(root, '[data-sr-detail-title]', item.dataset.title);
      text(root, '[data-sr-detail-description]', item.dataset.description);
      text(root, '[data-sr-detail-counter]', count());
      text(root, '[data-sr-detail-status]', item.dataset.price);
      const buy = root.querySelector('[data-sr-buy]');
      if (buy) { buy.hidden = !item.dataset.url; if (item.dataset.url) buy.href = item.dataset.url; }
      const draft = root.querySelector('[data-sr-draft]');
      if (draft) draft.hidden = Boolean(item.dataset.url);
      root.querySelectorAll('[data-sr-view]').forEach(button => { button.hidden = !item.dataset[button.dataset.srView]; });
      view(item.dataset.side === 'back' ? 'back' : 'front');
      detail.showModal();
      requestAnimationFrame(() => { if (detail.open) detail.classList.add('is-open'); });
    }
    function closeDetail() {
      if (!detail) return;
      detail.classList.remove('is-open');
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => detail.close(), reducedMotion.matches ? 0 : 180);
    }

    listen(previousButton, 'click', () => move(-1));
    listen(nextButton, 'click', () => move(1));
    listen(openButton, 'click', event => openDetail(event.currentTarget));
    listen(center, 'click', event => { if (!suppressClick) openDetail(event.currentTarget); });
    all.forEach(item => listen(item, 'click', event => {
      if (suppressClick) { event.preventDefault(); return; }
      const position = visible.indexOf(item);
      if (position < 0) return;
      select(position, Number(item.dataset.position) < 0 ? -1 : 1);
    }));
    listen(colorSelect, 'change', () => {
      const item = itemNow();
      const choice = choices.get(item)?.find(value => value.color === colorSelect.value);
      if (!choice) return;
      const previous = shown;
      for (const key of ['color', 'description', 'url', 'price', 'front', 'back', 'model', 'modelAlt', 'garment']) item.dataset[key] = choice[key] || '';
      item.dataset.side = choice.side === 'back' ? 'back' : 'front';
      applyMapping(item);
      paint();
      changeLook(previous, snapshot(item), 1);
    });
    root.querySelectorAll('[data-sr-filter]').forEach(button => listen(button, 'click', () => {
      const previous = shown;
      const filter = button.dataset.srFilter;
      visible = all.filter(item => filter === 'all' || item.dataset.category === filter);
      index = 0;
      root.querySelectorAll('[data-sr-filter]').forEach(tab => {
        tab.classList.toggle('is-active', tab === button);
        tab.setAttribute('aria-pressed', String(tab === button));
      });
      paint();
      if (itemNow()) changeLook(previous, snapshot(itemNow()), 1);
    }));
    listen(stage, 'keydown', event => {
      if (!visible.length || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
      if (event.key === 'Home') select(0, -1);
      if (event.key === 'End') select(visible.length - 1, 1);
    });

    function finishGesture(cancelled = false) {
      if (!drag) return;
      const ended = drag;
      drag = null;
      stage.classList.remove('is-dragging');
      stage.style.removeProperty('--sr-drag');
      if (stage.hasPointerCapture?.(ended.id)) stage.releasePointerCapture(ended.id);
      if (ended.active) {
        suppressClick = true;
        clearTimeout(releaseTimer);
        releaseTimer = setTimeout(() => { suppressClick = false; }, 350);
        const threshold = Math.max(28, Math.min(64, stage.clientWidth * .07));
        const velocity = Math.abs(ended.dx) / Math.max(1, performance.now() - ended.started);
        if (!cancelled && (Math.abs(ended.dx) > threshold || (Math.abs(ended.dx) > 18 && velocity > .45))) move(ended.dx < 0 ? 1 : -1);
      }
    }
    listen(stage, 'pointerdown', event => {
      if (visible.length < 2 || event.isPrimary === false || event.button !== 0 || drag) return;
      clearTimeout(releaseTimer);
      suppressClick = false;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, active: false, started: performance.now() };
    });
    listen(stage, 'pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * 1.15) { finishGesture(true); return; }
        if (Math.abs(dx) < 9 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
        drag.active = true;
        stage.classList.add('is-dragging');
        stage.setPointerCapture?.(drag.id);
      }
      if (event.cancelable) event.preventDefault();
      drag.dx = dx;
      suppressClick = true;
      stage.style.setProperty('--sr-drag', `${Math.max(-28, Math.min(28, dx * .12))}px`);
    }, { passive: false });
    listen(stage, 'pointerup', event => { if (drag?.id === event.pointerId) finishGesture(); });
    listen(stage, 'pointercancel', event => { if (drag?.id === event.pointerId) finishGesture(true); });
    listen(stage, 'lostpointercapture', event => { if (event.target === stage && drag?.id === event.pointerId) finishGesture(true); });
    listen(stage, 'click', event => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true });
    listen(stage, 'dragstart', event => event.preventDefault());
    listen(root.querySelector('[data-sr-close]'), 'click', closeDetail);
    listen(detail, 'cancel', event => { event.preventDefault(); closeDetail(); });
    listen(detail, 'click', event => {
      if (event.target !== detail) return;
      const box = detail.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeDetail();
    });
    listen(detail, 'close', () => { detail.classList.remove('is-open'); opener?.focus({ preventScroll: true }); });
    root.querySelectorAll('[data-sr-view]').forEach(button => listen(button, 'click', () => view(button.dataset.srView)));
    const motionChange = () => {
      if (reducedMotion.matches) stopTransition();
    };
    reducedMotion.addEventListener('change', motionChange);
    const cleanup = () => {
      stopTransition();
      for (const image of warmModels.values()) image.removeAttribute('src');
      warmModels.clear();
      clearTimeout(closeTimer);
      clearTimeout(releaseTimer);
      reducedMotion.removeEventListener('change', motionChange);
      abort.abort();
    };
    listen(root.closest('.shopify-section') || root, 'shopify:section:unload', cleanup, { once: true });
    paint();
    if (itemNow()) changeLook(null, snapshot(itemNow()), 1);
  }
  function mountAll(scope = document) {
    if (scope.matches?.('[data-sale-motion]')) mount(scope);
    scope.querySelectorAll('[data-sale-motion]').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  else mountAll();
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
})();
